/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角求解 class 层；
 * 2. 负责 SolidWorks Distance Distance 倒角的 Puppy CAD 内核 构建；
 * 3. 不处理 UI 模式切换，只消费已归一化定义。
 *
 * 文件职责：
 * 1. 校验距离-距离倒角参数；
 * 2. 对称模式走单距离 Add_2；
 * 3. 非对称模式走双距离 Add_3 并在 worker 层解析支撑面。
 */

import {
  resolveEdgeSelections,
} from '../../common';
import { resolveEdgeSupportFace } from '../../edge-support-face-resolution';
import {
  assertChamferBuilderResultSafe,
  buildProgressRange,
  safeDelete,
} from '../functions/chamferPuppyKernel';
import type { ChamferShapeBuildInput } from '../types/ChamferShapeBuildInput';
import { ChamferSolver } from './ChamferSolver';

/**
 * 距离-距离倒角求解器。
 */
export class DistanceDistanceChamferSolver extends ChamferSolver {
  
  public readonly type = 'distance-distance' as const;

  /**
   * @inheritdoc
   */
  public build(input: ChamferShapeBuildInput): unknown {
    const { puppy, sourceShape, sourceMetadata, sourceObjectId, definition } = input;
    if (typeof definition.distance !== 'number' || !Number.isFinite(definition.distance) || definition.distance <= 0) {
      throw new Error('距离-距离倒角缺少有效距离');
    }

    const builder = new puppy.BRepFilletAPI_MakeChamfer(sourceShape);
    const selections = resolveEdgeSelections(
      puppy,
      sourceShape,
      sourceMetadata,
      definition.items,
      { sourceObjectId },
    );
    const isSymmetric = definition.symmetric !== false;
    if (!isSymmetric && (
      typeof definition.distance2 !== 'number'
      || !Number.isFinite(definition.distance2)
      || definition.distance2 <= 0
    )) {
      throw new Error('距离-距离倒角的非对称模式缺少有效距离 2');
    }
    const progress = buildProgressRange(puppy);

    try {
      selections.forEach(({ edgeShape, selection }) => {
        if (isSymmetric) {
          builder.Add_2(definition.distance as number, edgeShape);
          return;
        }
        const supportFace = resolveEdgeSupportFace(puppy, sourceShape, sourceMetadata, selection, edgeShape);
        builder.Add_3(definition.distance as number, definition.distance2 as number, edgeShape, supportFace);
      });
      builder.Build(progress);
      return assertChamferBuilderResultSafe(puppy, builder);
    } finally {
      safeDelete(progress);
      safeDelete(builder);
    }
  }
}
